// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useCallback, useEffect, useRef, useState } from 'react';

import { trackCandidate, type Inference } from '../../bygning/inference';
import { EMPTY_TRACKING, dwellLeft, hoverTrack, type TrackPoint, type Tracking } from '../../bygning/smartGuides';

/**
 * The smart guides' hover-acquired tracking points for one tool layer
 * (frontend-u0o.12): `observe` each resolved inference (or a candidate
 * point); resting on an endpoint, midpoint or guide point acquires it
 * after `HOVER_MS` even when the pointer no longer moves. The set starts
 * afresh whenever `resetKey` changes (a new tool or command).
 */
export function useSmartTracking(resetKey: unknown) {
  const [state, setState] = useState<Tracking>(EMPTY_TRACKING);
  const ref = useRef<Tracking>(EMPTY_TRACKING);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const put = useCallback((s: Tracking) => {
    if (s === ref.current) return;
    ref.current = s;
    setState(s);
  }, []);
  const clearTimer = () => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
  };

  useEffect(() => {
    clearTimer();
    put(EMPTY_TRACKING);
  }, [resetKey, put]);
  useEffect(() => clearTimer, []);

  /** The pointer is over this point (or an inference that may name one), or over none. */
  const observe = useCallback(
    (what: Inference | TrackPoint | null) => {
      const cand = what === null ? null : 'kind' in what ? trackCandidate(what) : what;
      const now = performance.now();
      const next = hoverTrack(ref.current, cand, now);
      put(next);
      clearTimer();
      const wait = dwellLeft(next, now);
      if (wait !== null) {
        timer.current = setTimeout(() => {
          timer.current = null;
          const h = ref.current.hover;
          put(hoverTrack(ref.current, h ? h.point : null, performance.now()));
        }, wait + 10);
      }
    },
    [put],
  );

  /** The acquired points, current even inside a pointer handler. */
  const points = useCallback(() => ref.current.points, []);

  return { tracked: state.points, points, observe };
}
